'use client';

/**
 * Placeholder a hero/rotator block renders when it ends up with ZERO
 * slides/cards to show — either none were ever authored, or every one
 * got filtered out by its own campaign gate (expired, or audience-
 * restricted — see {@code CampaignAudience}/{@code useCampaignVisibility}
 * usage in each hero block). Previously each block improvised its own
 * handling here (an empty colored box, `return null`, or a "No slides
 * configured" diagnostic meant for the editor) — none of which is a
 * reasonable thing for a real visitor to see. Renders the tenant's brand
 * mark centered over a gradient directly — the same look course cards'
 * "no cover image" fallback uses, but without routing through
 * {@link CourseCoverImage} itself, since this never has an `image` to
 * try loading in the first place.
 */
import type { CSSProperties } from 'react';
import { useBrand, type BrandInfo } from '@akademi/site-builder-runtime';

const DEFAULT_GRADIENT =
  'linear-gradient(135deg, var(--color-brand-100, #26c6da) 0%, var(--color-brand-200, #006d82) 100%)';

export function HeroEmptyFallback({
  style,
  gradient = DEFAULT_GRADIENT,
}: {
  style?: CSSProperties;
  /** No-cover background. Override when the calling block already has an
   *  established gradient of its own. */
  gradient?: string;
}) {
  const brand = useBrand();
  return (
    <div style={{ position: 'relative', overflow: 'hidden', ...style }}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {(brand?.logo || brand?.logoDark) && <HeroCoverBrandMark brand={brand} />}
      </div>
    </div>
  );
}

function HeroCoverBrandMark({
  brand,
}: {
  brand: BrandInfo | null;
  className?: string;
  style?: CSSProperties;
}) {
  const alt = brand?.name || '';
  // Flex-centered by the gradient wrapper above — margin:0 auto here would
  // do nothing since <img> isn't a block box, which is what let the mark
  // drift off-center (and clip) in the first place.
  const imgStyle = {
    padding: '8%',
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain' as const,
  };

  return (
    <>
      {brand?.logo && brand?.logoDark ? (
        <>
          <img className="lms-logo--light" src={brand.logo} alt={alt} style={imgStyle} />
          <img className="lms-logo--dark" src={brand.logoDark} alt={alt} style={imgStyle} />
        </>
      ) : (
        <img src={brand?.logo || brand?.logoDark} alt={alt} style={imgStyle} />
      )}
    </>
  );
}
